import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Calendar,
  CalendarDays,
  BookOpen,
  Target,
  FileText,
  Clock,
  ChevronRight,
  Plus,
  Eye,
  Download,
  Loader2,
  Activity,
  ListTodo,
  CreditCard,
  ClipboardList,
  Bell,
  History,
  MessageCircle,
  type LucideIcon,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  NumericMetricDescriptionInfo,
} from "@cliently/ui";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { mergePortalDocumentDownloadConfig } from "../lib/portal-document-download";
import {
  type AppointmentStatus,
  portalProfessionTranslationKey,
} from "@cliently/types";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { resolveNumericTrackingConfigured } from "@cliently/types";
import type { ClientPortalFeatures } from "@cliently/schemas";
import { useOrganizations } from "../hooks/use-organizations";
import { parseNumericMetricEntries } from "../lib/numeric-metric-entries";
import { SelectedOptionsIllustration } from "../assets/undraw/SelectedOptionsIllustration";
import { NoDataIllustration } from "../assets/undraw/NoDataIllustration";
import { EmptyIllustration } from "../assets/undraw/empty";
import type { PortalServiceItem } from "../types/portal-service-item";
import {
  type PortalTimelineItem,
  usePortalTimeline,
} from "../hooks/use-portal-timeline";

const TIMELINE_PREVIEW_ROWS = 3;

/** Pastilles type (Document, Rendez-vous, …) : même largeur dans l’aperçu historique. */
const TIMELINE_KIND_BADGE_WIDTH_PX = 120;



const parseAssignedMetricConfig = (
  meta: unknown,
  metricGroups: Array<{ metricIds: string[] }> | undefined,
): {
  configured: boolean;
  ids: string[];
} => {
  const root =
    meta && typeof meta === "object" && !Array.isArray(meta)
      ? (meta as Record<string, unknown>)
      : {};
  const priv =
    root.private && typeof root.private === "object" && !Array.isArray(root.private)
      ? (root.private as Record<string, unknown>)
      : {};
  const raw = priv.numericMetricDefinitionIds;
  const directConfigured = Array.isArray(raw);
  const directIds = directConfigured
    ? (raw as unknown[]).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      )
    : [];
  const groups = (metricGroups ?? []).map((g) => g.metricIds ?? []);
  const hasGroups = groups.some((arr) => arr.length > 0);
  const configured = directConfigured || hasGroups;
  if (!configured) return { configured: false, ids: [] };
  const set = new Set<string>(directIds);
  for (const arr of groups)
    for (const id of arr) {
      if (typeof id === "string" && id.length > 0) set.add(id);
    }
  return { configured: true, ids: Array.from(set) };
}



const dashboardTimelineKindIcon = (kind: PortalTimelineItem["kind"]) => {
  switch (kind) {
    case "appointment":
      return Calendar;
    case "invoice":
      return CreditCard;
    case "document":
      return FileText;
    case "request":
      return ClipboardList;
    default:
      return MessageCircle;
  }
}



const dashboardTimelineKindVariant = (kind: PortalTimelineItem["kind"]): "default" | "secondary" | "outline" => {
  switch (kind) {
    case "appointment":
      return "default";
    case "invoice":
      return "secondary";
    default:
      return "outline";
  }
}

interface DashboardAppointment {
  id: string;
  startsAt: string;
  status: string;
  duration: number;
  type?: string;
  serviceItemId?: string | null;
  org?: { id: string; name: string };
}

interface DashboardObjective {
  id: string;
  title: string;
  progress: number;
  doneAt: string | null;
  target?:
    | { type: "NUMBER"; value: number; unit?: string }
    | { type: "DURATION"; value: number; unit?: "seconds" }
    | { type: "BOOLEAN"; value: boolean; unit?: string }
    | { type: "AMOUNT"; value: number; unit?: string }
    | { type: "FREQUENCY"; value: number; unit?: string }
    | null;
  client?: { org?: { id: string; name: string } };
}



const formatDashboardTarget = (target: DashboardObjective["target"]): string | null => {
  if (!target) return null;
  if (target.type === "BOOLEAN") return target.value ? "Oui" : "Non";
  if (target.type === "DURATION") {
    const totalSeconds = Math.max(0, Math.round(target.value));
    return `${Math.floor(totalSeconds / 60)} min ${totalSeconds % 60}s`;
  }
  if (target.type === "AMOUNT")
    return `${target.value} ${target.unit ?? "EUR"}`;
  if (target.type === "FREQUENCY")
    return `${target.value} ${target.unit ?? "par semaine"}`;
  return `${target.value} ${target.unit ?? "unités"}`;
}

interface DashboardDocument {
  id: string;
  name: string;
  label?: string | null;
  url: string;
  type: string;
  clientId?: string;
  org?: { id: string; name: string } | null;
}

const dashboardDocTitle = (doc: DashboardDocument) => {
  const l = doc.label?.trim();
  return l && l.length > 0 ? l : doc.name;
}

/** Couleurs d’icône uniquement — même esprit que StatCard du dashboard pro (sans fond). */
const KPI_ICON_COLORS = {
  sky: "text-sky-600 dark:text-sky-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  violet: "text-violet-600 dark:text-violet-400",
  amber: "text-amber-700 dark:text-amber-400",
  rose: "text-rose-600 dark:text-rose-400",
} as const;

type KpiAccent = keyof typeof KPI_ICON_COLORS;



const ClientDashboardKpiCard = ({
  label,
  value,
  icon: Icon,
  accent,
  to,
}: {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  accent: KpiAccent;
  to: string;
}) => {
  const iconColor = KPI_ICON_COLORS[accent];
  return (
    <Link to={to} aria-label={label} className="block">
      <Card className="p-4 transition-colors hover:bg-muted/40 sm:p-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9"
            aria-hidden
          >
            <Icon className={cn("h-4 w-4 sm:h-5 sm:w-5", iconColor)} />
          </div>
          <div className="min-w-0 flex-1 space-y-1 pt-0.5">
            <p className="text-muted-foreground truncate text-xs leading-tight sm:text-sm">
              {label}
            </p>
            <p className="text-2xl font-semibold tabular-nums leading-none tracking-tight">
              {value}
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
}



const dashboardPreviewKind = (mime: string, doc: DashboardDocument): "image" | "pdf" | null => {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (!mime || mime === "application/octet-stream") {
    const kind = doc.type?.toLowerCase() ?? "";
    if (kind === "image" || kind.startsWith("image")) return "image";
    if (kind === "pdf" || kind.includes("pdf")) return "pdf";
  }
  return null;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;



const startOfWeekMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}



const buildWeeklyAppointmentSeries = (appointments: DashboardAppointment[], locale: string, weekCount: number): { label: string; count: number }[] => {
  const filtered = appointments.filter((a) => a.status !== "CANCELLED");
  const currentWeekStart = startOfWeekMonday(new Date());
  const buckets: { start: number; label: string; count: number }[] = [];
  for (let i = weekCount - 1; i >= 0; i--) {
    const ws = new Date(currentWeekStart.getTime() - i * WEEK_MS);
    buckets.push({
      start: ws.getTime(),
      label: ws.toLocaleDateString(locale, { day: "numeric", month: "short" }),
      count: 0,
    });
  }
  for (const a of filtered) {
    const t = new Date(a.startsAt).getTime();
    for (let i = 0; i < buckets.length; i++) {
      const bucket = buckets[i];
      if (!bucket) continue;
      const end = bucket.start + WEEK_MS;
      if (t >= bucket.start && t < end) {
        bucket.count++;
        break;
      }
    }
  }
  return buckets.map(({ label, count }) => ({ label, count }));
}



const useClientDashboard = (activeClientId: string | null) => {
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  return useQuery({
    queryKey: ["client-dashboard", activeClientId],
    queryFn: async () => {
      const [appts, objectives, profile, programs] = await Promise.all([
        api
          .get(
            isAllProsMode
              ? "/portal/aggregate/appointments"
              : "/portal/appointments",
          )
          .then((r) => r.data),
        api
          .get(
            isAllProsMode
              ? "/portal/aggregate/objectives"
              : "/portal/objectives",
          )
          .then((r) => r.data)
          .catch(() => []),
        api
          .get(isAllProsMode ? "/portal/aggregate/me" : "/portal/me")
          .then((r) => r.data)
          .catch(() => null),
        api
          .get(
            isAllProsMode ? "/portal/aggregate/programs" : "/portal/programs",
          )
          .then((r) => r.data)
          .catch(() => []),
      ]);

      const programAssignmentsCount = Array.isArray(programs)
        ? programs.length
        : 0;

      if (isAllProsMode) {
        return {
          appointments: appts,
          objectives,
          profile,
          programAssignmentsCount,
          serviceItems: [] as PortalServiceItem[],
          invoices: [] as Array<{ id: string; status: string; number: string }>,
          intake: null as {
            needsUpdate?: boolean;
            intakeForm?: unknown[] | null;
          } | null,
          unreadNotifications: 0,
        };
      }

      const [serviceItems, invoices, intake, unreadRes] = await Promise.all([
        api.get("/portal/service-items").then((r) => r.data).catch(() => []),
        api.get("/portal/invoices").then((r) => r.data).catch(() => []),
        api.get("/portal/intake").then((r) => r.data).catch(() => null),
        api
          .get("/portal/notifications/unread-count")
          .then((r) => r.data)
          .catch(() => ({ count: 0 })),
      ]);

      return {
        appointments: appts,
        objectives,
        profile,
        programAssignmentsCount,
        serviceItems: serviceItems as PortalServiceItem[],
        invoices: invoices as Array<{ id: string; status: string; number: string }>,
        intake: intake as {
          needsUpdate?: boolean;
          intakeForm?: unknown[] | null;
        } | null,
        unreadNotifications:
          typeof unreadRes?.count === "number" ? unreadRes.count : 0,
      };
    },
  });
}

const statusColors: Record<AppointmentStatus, string> = {
  PENDING: "bg-yellow-500",
  CONFIRMED: "bg-blue-500",
  DONE: "bg-green-500",
  CANCELLED: "bg-red-500",
};



export const DashboardPage = () => {
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: organizations = [] } = useOrganizations();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const { data, isLoading } = useClientDashboard(activeClientId);
  const { data: timelineItems = [], isLoading: timelineLoading } =
    usePortalTimeline(activeClientId);
  const timelinePreview = timelineItems.slice(0, TIMELINE_PREVIEW_ROWS);

  const profile = data?.profile;
  const org = profile?.org;

  const now = new Date();
  const upcoming = (data?.appointments || [])
    .filter(
      (a: DashboardAppointment) =>
        new Date(a.startsAt) >= now && a.status !== "CANCELLED",
    )
    .sort(
      (a: DashboardAppointment, b: DashboardAppointment) =>
        new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    )
    .slice(0, 5);

  const activeObjectives = (data?.objectives || [])
    .filter((o: DashboardObjective) => !o.doneAt)
    .slice(0, 3);
  const pendingPaymentsCount = (data?.invoices || []).filter(
    (inv) => inv.status === "SENT",
  ).length;

  const recentDocs = (profile?.documents || []).slice(0, 3);

  const weeklyAppointmentSeries = useMemo(
    () => buildWeeklyAppointmentSeries(data?.appointments ?? [], locale, 12),
    [data?.appointments, locale],
  );

  const hasAppointmentActivity = weeklyAppointmentSeries.some(
    (w) => w.count > 0,
  );

  const metricDashboardCards = useMemo(() => {
    const features = profile?.org?.clientPortalFeatures as
      | ClientPortalFeatures
      | null
      | undefined;
    if (!features?.numericDefinitions?.length) return [];
    const allDefs = features.numericDefinitions;
    const assigned = parseAssignedMetricConfig(
      profile?.metaData,
      profile?.metricGroups,
    );
    const defs = allDefs.filter((def) => assigned.ids.includes(def.id));
    const entries = parseNumericMetricEntries(profile?.metaData);
    return defs.map((def) => {
      const latest = entries
        .filter((e) => e.metricId === def.id)
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
      return { def, latest };
    });
  }, [
    profile?.metaData,
    profile?.metricGroups,
    profile?.org?.clientPortalFeatures,
  ]);

  const dashboardTodos = useMemo(() => {
    if (isAllProsMode || !data) return [];
    const rows: Array<{
      key: string;
      title: string;
      hint?: string;
      href: string;
      icon: LucideIcon;
    }> = [];

    if (
      data.intake?.needsUpdate &&
      Array.isArray(data.intake.intakeForm) &&
      data.intake.intakeForm.length > 0
    ) {
      rows.push({
        key: "intake",
        title: t(
          "clientDashboard.todoIntakeTitle",
          "Compléter votre questionnaire",
        ),
        hint: t(
          "clientDashboard.todoIntakeHint",
          "Informations requises pour poursuivre.",
        ),
        href: "/appointments",
        icon: ClipboardList,
      });
    }

    const unpaid = data.invoices.find((inv) => inv.status === "SENT");
    if (unpaid) {
      rows.push({
        key: `inv-${unpaid.id}`,
        title: t("clientDashboard.todoPaymentTitle", "Régler une facture"),
        hint: t("clientDashboard.todoPaymentHint", "Facture {{n}}", {
          n: unpaid.number,
        }),
        href: "/payments",
        icon: CreditCard,
      });
    }

    if (data.unreadNotifications > 0) {
      rows.push({
        key: "notif",
        title: t(
          "clientDashboard.todoNotifTitle",
          "Notifications à consulter",
        ),
        hint: t(
          "clientDashboard.todoNotifHint",
          "{{count}} non lue(s)",
          { count: data.unreadNotifications },
        ),
        href: "/timeline",
        icon: Bell,
      });
    }

    const na = upcoming[0] as DashboardAppointment | undefined;
    if (na?.serviceItemId && data.serviceItems?.length) {
      const svc = data.serviceItems.find((s) => s.id === na.serviceItemId);
      const notes = svc?.clientPreparationNotes?.trim();
      if (notes) {
        const ms = new Date(na.startsAt).getTime() - Date.now();
        if (ms > 0 && ms < 7 * 86400000) {
          rows.push({
            key: "prep",
            title: t(
              "clientDashboard.todoPrepTitle",
              "Préparer votre séance",
            ),
            hint: notes.length > 100 ? `${notes.slice(0, 97)}…` : notes,
            href: `/appointments?appointmentId=${encodeURIComponent(na.id)}`,
            icon: Calendar,
          });
        }
      }
    }

    return rows.slice(0, 4);
  }, [isAllProsMode, data, upcoming, t]);

  const [previewDoc, setPreviewDoc] = useState<DashboardDocument | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);

  const closeDocumentPreview = useCallback(() => {
    setPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewMime("");
    setPreviewDoc(null);
    setPreviewLoading(false);
  }, []);

  const openDocumentPreview = async (doc: DashboardDocument) => {
    setPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewMime("");
    setPreviewDoc(doc);
    setPreviewLoading(true);
    try {
      const { data: blob } = await api.get(
        `/portal/documents/${doc.id}/download`,
        mergePortalDocumentDownloadConfig(isAllProsMode, doc.clientId, {
          responseType: "blob",
        }),
      );
      setPreviewBlobUrl(URL.createObjectURL(blob));
      setPreviewMime(blob.type || "");
    } catch {
      closeDocumentPreview();
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadDocument = async (doc: DashboardDocument) => {
    const { data: blob } = await api.get(
      `/portal/documents/${doc.id}/download`,
      mergePortalDocumentDownloadConfig(isAllProsMode, doc.clientId, {
        responseType: "blob",
      }),
    );

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = doc.name;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="bg-muted h-8 w-48 max-w-full animate-pulse rounded" />
          <div className="bg-muted mt-2 h-4 w-80 max-w-full animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card
              key={`kpi-skel-${i}`}
              className="bg-muted h-24 animate-pulse sm:h-28"
            />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="bg-muted h-48 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const documentPreviewKind =
    previewDoc && previewBlobUrl && !previewLoading
      ? dashboardPreviewKind(previewMime, previewDoc)
      : null;

  let documentPreviewBody: ReactNode = null;
  if (previewLoading) {
    documentPreviewBody = (
      <Loader2 className="text-muted-foreground h-10 w-10 animate-spin" />
    );
  } else if (previewBlobUrl && previewDoc) {
    if (documentPreviewKind === "image") {
      documentPreviewBody = (
        <img
          src={previewBlobUrl}
          alt=""
          className="max-h-[min(70vh,800px)] w-auto max-w-full rounded-md object-contain"
        />
      );
    } else if (documentPreviewKind === "pdf") {
      documentPreviewBody = (
        <iframe
          title={dashboardDocTitle(previewDoc)}
          src={previewBlobUrl}
          className="border-input h-[min(70vh,800px)] w-full rounded-md border"
        />
      );
    } else {
      documentPreviewBody = (
        <p className="text-muted-foreground text-center text-sm">
          {t("clientDashboard.previewUnsupported")}
        </p>
      );
    }
  }

  const followedByLabel = org ? (
    <span className="inline-flex flex-wrap items-baseline gap-x-1">
      <span>{t("clientDashboard.followedBy")}</span>
      <span className="text-foreground font-medium">
        {isAllProsMode
          ? t("clientDashboard.practitionersCount", {
              count: organizations.length,
            })
          : org.name}
      </span>
      {!isAllProsMode && org.professionType && (
        <span>
          &middot;{" "}
          {org.professionType === "GENERIC"
            ? org.customProfession || t("portal.practitioner")
            : (() => {
                const key = portalProfessionTranslationKey(org.professionType);
                return key ? t(key) : org.professionType;
              })()}
        </span>
      )}
    </span>
  ) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("clientDashboard.title", "Mon espace")}
        </h1>
        {followedByLabel ? (
          <p className="text-muted-foreground mt-1 text-sm">
            {followedByLabel}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div className="min-w-0">
          <ClientDashboardKpiCard
            label={t("clientDashboard.kpiUpcoming")}
            value={upcoming.length}
            icon={Calendar}
            accent="sky"
            to="/appointments"
          />
        </div>
        <div className="min-w-0">
          <ClientDashboardKpiCard
            label={t("clientDashboard.kpiObjectives")}
            value={activeObjectives.length}
            icon={Target}
            accent="emerald"
            to="/objectives"
          />
        </div>
        <div className="min-w-0">
          <ClientDashboardKpiCard
            label={t("clientDashboard.kpiPrograms")}
            value={data?.programAssignmentsCount ?? 0}
            icon={BookOpen}
            accent="amber"
            to="/programs"
          />
        </div>
        <div className="min-w-0">
          <ClientDashboardKpiCard
            label={t("clientDashboard.kpiTotal")}
            value={data?.appointments?.length ?? 0}
            icon={CalendarDays}
            accent="violet"
            to="/appointments"
          />
        </div>
        <div className="min-w-0">
          <ClientDashboardKpiCard
            label={t("clientDashboard.kpiPendingPayments")}
            value={pendingPaymentsCount}
            icon={CreditCard}
            accent="rose"
            to="/payments"
          />
        </div>
      </div>

      <div className="w-full min-w-0">
        <Card className="min-w-0 w-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4" />
              {t("clientDashboard.chartAppointmentsTitle")}
            </CardTitle>
            <p className="text-muted-foreground mt-1 text-xs">
              {t("clientDashboard.chartAppointmentsDesc")}
            </p>
          </CardHeader>
          <CardContent>
            {hasAppointmentActivity ? (
              <div className="h-[min(260px,38vh)] w-full min-w-0 lg:h-[min(280px,40vh)]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={weeklyAppointmentSeries}
                    margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
                  >
                    <defs>
                      <linearGradient
                        id="dashboardApptArea"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="var(--color-primary)"
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor="var(--color-primary)"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-border/80"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      height={32}
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                    />
                    <YAxis
                      allowDecimals={false}
                      width={28}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12 }}
                      formatter={(value: number | string) => [
                        value,
                        t("clientDashboard.chartSessionCount"),
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      name={t("clientDashboard.chartSessionCount")}
                      stroke="var(--color-primary)"
                      strokeWidth={2}
                      fill="url(#dashboardApptArea)"
                      dot={{ r: 2, fill: "var(--color-primary)" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-muted-foreground py-10 text-center text-sm">
                {t("clientDashboard.chartAppointmentsEmpty")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {!isAllProsMode && dashboardTodos.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ListTodo className="h-4 w-4" />
              {t("clientDashboard.todosTitle", "À faire pour moi")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {dashboardTodos.map((todo) => {
              const Icon = todo.icon;
              return (
                <Button
                  key={todo.key}
                  variant="outline"
                  className="h-auto w-full justify-between gap-3 py-3"
                  asChild
                >
                  <Link to={todo.href}>
                    <span className="flex min-w-0 items-start gap-3 text-left">
                      <Icon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">
                          {todo.title}
                        </span>
                        {todo.hint ? (
                          <span className="text-muted-foreground mt-0.5 line-clamp-2 block text-xs leading-snug">
                            {todo.hint}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                  </Link>
                </Button>
              );
            })}
          </CardContent>
        </Card>
      ) : null}

      {profile?.org &&
      resolveNumericTrackingConfigured(
        profile.org.clientPortalFeatures as ClientPortalFeatures | null,
      ) &&
      !isAllProsMode &&
      metricDashboardCards.length > 0 ? (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 font-semibold tracking-tight">
                <Activity className="h-4 w-4" />
                {t("clientDashboard.metricsCtaTitle", "Suivi personnalisé")}
              </h2>
              <p className="text-muted-foreground mt-1 text-xs">
                {t(
                  "clientDashboard.metricsCtaDesc",
                  "Saisissez vos métriques et consultez les courbes sur 90 jours.",
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/metrics">
                {t("clientDashboard.metricsCtaBtn", "Voir mes métriques")}
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {metricDashboardCards.map(({ def, latest }) => (
              <Card
                key={def.id}
                className="hover:bg-muted/45 h-full p-4 transition-colors"
              >
                <div className="flex items-start gap-1">
                  <Link
                    to="/metrics"
                    className="focus-visible:ring-ring block min-w-0 flex-1 rounded-lg outline-none focus-visible:ring-2"
                  >
                    <p className="text-muted-foreground text-xs leading-snug">
                      {def.label}
                    </p>
                    <p className="mt-2 text-2xl font-bold tabular-nums">
                      {latest ? (
                        <>
                          {latest.value}
                          {def.unit ? (
                            <span className="text-muted-foreground ml-1 text-base font-semibold">
                              {def.unit}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-muted-foreground text-lg font-normal">
                          {t("clientDashboard.metricNoValue", "—")}
                        </span>
                      )}
                    </p>
                    {latest ? (
                      <p className="text-muted-foreground mt-2 text-xs">
                        {t(
                          "clientDashboard.metricLastAt",
                          "Dernière saisie : {{date}}",
                          {
                            date: new Date(latest.at).toLocaleString(locale, {
                              dateStyle: "medium",
                              timeStyle: "short",
                            }),
                          },
                        )}
                      </p>
                    ) : (
                      <p className="text-muted-foreground mt-2 text-xs">
                        {t(
                          "clientDashboard.metricNever",
                          "Aucune valeur encore saisie.",
                        )}
                      </p>
                    )}
                  </Link>
                  <NumericMetricDescriptionInfo
                    description={def.description}
                    ariaLabel={t(
                      "metrics.definitionInfoAria",
                      "Informations sur cette métrique",
                    )}
                  />
                </div>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              {t("clientDashboard.upcomingAppts")}
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/appointments">
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {upcoming.length > 0 ? (
              <div className="space-y-3">
                {upcoming.slice(0, 3).map((appt: DashboardAppointment) => (
                  <div key={appt.id} className="flex items-center gap-3">
                    <div
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-lg text-white",
                        statusColors[appt.status as AppointmentStatus],
                      )}
                    >
                      <Clock className="h-4 w-4" />
                    </div>
                    <div className="flex-1 text-sm">
                      <p className="font-medium">
                        {new Date(appt.startsAt).toLocaleDateString(locale, {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </p>
                      <p className="text-muted-foreground">
                        {new Date(appt.startsAt).toLocaleTimeString(locale, {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" · "}
                        {appt.duration} min
                        {appt.org?.name ? ` · ${appt.org.name}` : ""}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {appt.type || "RDV"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <EmptyIllustration
                  size={160}
                  className="text-muted-foreground mx-auto max-w-full"
                />
                <p className="text-muted-foreground text-sm">
                  {t("clientDashboard.noUpcoming")}
                </p>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/appointments">
                    <Plus className="mr-2 h-3 w-3" />
                    {t("clientDashboard.book")}
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="h-4 w-4" />
              {t("clientDashboard.objectives")}
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/objectives">
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {activeObjectives.length > 0 ? (
              <div className="space-y-3">
                {activeObjectives.map((obj: DashboardObjective) => (
                  <div key={obj.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate font-medium">{obj.title}</span>
                      <span className="text-muted-foreground">
                        {obj.progress}%
                      </span>
                    </div>
                    {formatDashboardTarget(obj.target) ? (
                      <p className="text-muted-foreground text-[11px]">
                        Cible : {formatDashboardTarget(obj.target)}
                      </p>
                    ) : null}
                    {isAllProsMode && obj.client?.org?.name ? (
                      <p className="text-muted-foreground text-[11px]">
                        {obj.client.org.name}
                      </p>
                    ) : null}
                    <div className="bg-muted h-1.5 w-full rounded-full">
                      <div
                        className={cn(
                          "h-1.5 rounded-full transition-all",
                          obj.progress >= 50 ? "bg-primary" : "bg-orange-400",
                        )}
                        style={{ width: `${obj.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <SelectedOptionsIllustration size={112} className="mx-auto" />
                <p className="text-muted-foreground text-sm">
                  {t("clientDashboard.noObjectives")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex min-w-0 items-center gap-2 text-base">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {t("clientDashboard.documents")}
              </span>
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/profile">
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="min-w-0">
            {recentDocs.length > 0 ? (
              <div className="space-y-2">
                {recentDocs.map((doc: DashboardDocument) => (
                  <div
                    key={doc.id}
                    className="hover:bg-muted/45 flex min-w-0 flex-wrap items-center gap-2 rounded-lg p-2 text-sm"
                  >
                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span
                      className="min-w-0 flex-1 truncate font-medium"
                      title={dashboardDocTitle(doc)}
                    >
                      {dashboardDocTitle(doc)}
                    </span>
                    {isAllProsMode && doc.org?.name ? (
                      <span className="text-muted-foreground max-w-22 shrink truncate text-[10px]">
                        {doc.org.name}
                      </span>
                    ) : null}
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {doc.type}
                    </Badge>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 p-0"
                      onClick={() => void openDocumentPreview(doc)}
                      aria-label={t("profile.previewDocument")}
                      title={t("profile.previewDocument")}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 p-0"
                      onClick={() => void downloadDocument(doc)}
                      aria-label={t("profile.downloadDocument")}
                      title={t("profile.downloadDocument")}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <NoDataIllustration size={112} className="mx-auto" />
                <p className="text-muted-foreground text-sm">
                  {t("clientDashboard.noDocuments")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      <Card className="min-w-0">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4 shrink-0" />
            {t("timeline.title", "Historique")}
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link
              to="/timeline"
              aria-label={t(
                "clientDashboard.historySeeAll",
                "Voir tout l'historique",
              )}
            >
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {timelineLoading ? (
            <div className="space-y-3">
              {Array.from({ length: TIMELINE_PREVIEW_ROWS }).map((_, i) => (
                <div
                  key={i}
                  className="bg-muted h-14 animate-pulse rounded-lg"
                />
              ))}
            </div>
          ) : timelinePreview.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {t("timeline.empty", "Aucun événement pour le moment.")}
            </p>
          ) : (
            <div className="px-2 py-2">
              {timelinePreview.map((item, idx) => {
                const Icon = dashboardTimelineKindIcon(item.kind);
                const labelKind = t(`timeline.kind.${item.kind}`, item.kind);
                return (
                  <Link
                    key={`${item.id}-${idx}`}
                    to={item.href}
                    className={cn(
                      "hover:bg-muted/45 focus-visible:ring-ring flex flex-col gap-2 rounded-lg px-2 py-3 outline-none transition-colors focus-visible:ring-2",
                      idx < timelinePreview.length - 1 &&
                        "border-border border-b",
                    )}
                  >
                    <div className="flex gap-3">
                      <div className="min-w-0 flex-1">
                        <time className="text-muted-foreground block text-xs">
                          {new Date(item.at).toLocaleString(locale, {
                            dateStyle: "medium",
                            timeStyle:
                              item.kind === "appointment"
                                ? "short"
                                : undefined,
                          })}
                        </time>
                        <p className="mt-0.5 truncate text-sm font-medium">
                          {item.title}
                        </p>
                        {item.subtitle ? (
                          <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                            {item.subtitle}
                          </p>
                        ) : null}
                        {item.orgName ? (
                          <p className="text-muted-foreground mt-0.5 text-[10px]">
                            {item.orgName}
                          </p>
                        ) : null}
                      </div>
                      <ChevronRight className="text-muted-foreground mt-1 h-4 w-4 shrink-0 self-start opacity-60" />
                    </div>
                    <Badge
                      variant={dashboardTimelineKindVariant(item.kind)}
                      style={{
                        width: TIMELINE_KIND_BADGE_WIDTH_PX,
                        minWidth: TIMELINE_KIND_BADGE_WIDTH_PX,
                        maxWidth: TIMELINE_KIND_BADGE_WIDTH_PX,
                      }}
                      className="pointer-events-none inline-flex h-7 shrink-0 items-center justify-center gap-1 px-2 text-[10px]"
                    >
                      <Icon className="h-3 w-3 shrink-0" />
                      <span className="min-w-0 truncate">{labelKind}</span>
                    </Badge>
                  </Link>
                );
              })}
              <Link
                to="/timeline"
                className="text-muted-foreground hover:text-foreground hover:bg-muted/45 focus-visible:ring-ring mt-1 block rounded-lg px-2 py-3 text-center text-xs font-medium outline-none transition-colors focus-visible:ring-2"
              >
                {t(
                  "clientDashboard.historySeeAll",
                  "Voir tout l'historique",
                )}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={previewDoc !== null}
        onOpenChange={(open) => {
          if (!open) closeDocumentPreview();
        }}
      >
        <DialogContent className="max-w-4xl gap-0">
          <DialogHeader>
            <DialogTitle className="pr-8">
              {previewDoc ? dashboardDocTitle(previewDoc) : ""}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex min-h-[200px] items-center justify-center pt-2">
            {documentPreviewBody}
          </DialogBody>
          {previewDoc && !previewLoading ? (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => void downloadDocument(previewDoc)}
              >
                <Download className="mr-2 h-4 w-4" />
                {t("profile.downloadDocument")}
              </Button>
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
